import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

/**
 * Remove a currently-installed serialized component from its installation
 * point AND set its new status. Closes the open service_events row so
 * "time in service" math stays honest.
 *
 * The user MUST pick a new status — otherwise the part is in limbo.
 */
const RemoveComponentDialog = ({ open, onClose, onSuccess, component, installationPoint, equipmentName }) => {
  const [newStatus, setNewStatus] = useState('available');
  const [removedAt, setRemovedAt] = useState(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setNewStatus('available');
      setReason('');
      setNotes('');
      const d = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      setRemovedAt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        new_status: newStatus,
        removed_at: removedAt ? new Date(removedAt).toISOString() : null,
        removal_reason: reason.trim() || null,
        notes: notes.trim() || null,
      };
      await axios.post(`${API}/serialized-components/${component.id}/remove`, payload);
      toast.success(`Removed — now ${newStatus.replace('_', ' ')}`);
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remove failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !component) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="remove-component-dialog">
        <DialogHeader>
          <DialogTitle>Remove Part</DialogTitle>
          <DialogDescription asChild>
            <div>
              <div>
                <span className="font-medium">{component.name}</span>{' '}
                <span className="font-mono text-xs bg-slate-100 rounded px-1.5 py-0.5">
                  SN: {component.serial_number}
                </span>
              </div>
              <div className="text-xs mt-1">
                from {equipmentName} · <span className="font-medium">{installationPoint?.label}</span>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>New status *</Label>
            <div className="grid grid-cols-1 gap-2">
              {[
                {
                  v: 'available',
                  title: 'Available',
                  desc: 'Still serviceable — goes back to the shelf, ready for re-install.',
                },
                {
                  v: 'awaiting_rebuild',
                  title: 'Awaiting Rebuild',
                  desc: 'Needs work before it can be used again. Blocked from install until flipped back to Available.',
                },
                {
                  v: 'decommissioned',
                  title: 'Decommissioned',
                  desc: 'End of life. Kept in the record for service-history reporting but not installable.',
                },
              ].map((opt) => (
                <label
                  key={opt.v}
                  className={
                    'border rounded-md p-2.5 flex gap-2 cursor-pointer transition ' +
                    (newStatus === opt.v
                      ? 'border-slate-900 bg-slate-50'
                      : 'border-slate-200 hover:border-slate-400')
                  }
                  data-testid={`remove-status-${opt.v}`}
                >
                  <input
                    type="radio"
                    name="remove-status"
                    className="mt-1"
                    value={opt.v}
                    checked={newStatus === opt.v}
                    onChange={() => setNewStatus(opt.v)}
                  />
                  <div>
                    <div className="text-sm font-semibold">{opt.title}</div>
                    <div className="text-xs text-slate-600">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rc-removed-at">Removed time</Label>
            <Input
              id="rc-removed-at"
              data-testid="rc-removed-at-input"
              type="datetime-local"
              value={removedAt}
              onChange={(e) => setRemovedAt(e.target.value)}
            />
            <p className="text-xs text-slate-500">
              This stops the &ldquo;time in service&rdquo; clock. Cannot be earlier
              than the install time.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rc-reason">Reason (recommended)</Label>
            <Input
              id="rc-reason"
              data-testid="rc-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder='e.g. "Leaking at high pressure", "Scheduled PM"'
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rc-notes">Notes</Label>
            <Textarea
              id="rc-notes"
              data-testid="rc-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} data-testid="rc-submit-btn">
              {submitting ? 'Removing…' : 'Remove'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RemoveComponentDialog;
